import { MarketTemplateSettingsPropsType } from 'src/components/MarketTemplateSettingsForm/MarketTemplateSettingsForm';
import { SdkStaff } from 'src/AppState/sdk/staff';
import { SelectionType } from 'src/components/MarketTemplateSettingsForm/Components/MarketTemplateSelectionSettings';
import { OpenapiProxyStaffMarketTemplateUpdateParamsType } from 'src/api_openapi/generated/openapi_proxy_staff_market_template_update';
import { OpenapiProxyStaffMarketTemplateSelectionsUpdateParamsType } from 'src/api_openapi/generated/openapi_proxy_staff_market_template_selections_update';
import { OnChangeMeta } from 'react-movable';
import { OpenapiProxyStaffGetMarketTemplatesResponse200Type } from 'src/api_openapi/generated/openapi_proxy_staff_get_market_templates';
import {
    buildSelectionsNewTrading,
    SelectionNewTradingType,
} from 'src/components/MarketTemplateSettingsForm/Components/MarketTemplateSelections';
import { computed } from 'src_common/common/mobx-wrapper';
import { MarketTemplateItem } from 'src/components/SportPopup/SportMarketTemplates/SportMarketTemplatesItem';
import { PromiseExecutorQueue } from 'src_common/common/promiseExecutorQueue';
import { isBoolean } from 'lodash';

// eslint-disable-next-line @typescript-eslint/naming-convention
export type UpdateTemplateBody = OpenapiProxyStaffMarketTemplateUpdateParamsType['requestBody']['data'];
// eslint-disable-next-line @typescript-eslint/naming-convention
export type UpdateSelectionsBody = OpenapiProxyStaffMarketTemplateSelectionsUpdateParamsType['requestBody']['data'];
export type MarketTemplateSelectionsArrayType =
    OpenapiProxyStaffGetMarketTemplatesResponse200Type['results'] extends Array<infer Inner> ? Inner : never;
type MarketTemplateSelectionsType = MarketTemplateSelectionsArrayType['selections'];
type MarketTemplateSingleSelectionType = MarketTemplateSelectionsType extends Array<infer Inner> ? Inner : never;

function swapElements(
    array: MarketTemplateSelectionsType | undefined,
    index1: number,
    index2: number
): MarketTemplateSelectionsType {
    if (array === undefined) {
        return [];
    }
    const temp = array[index1];
    const temp2 = array[index2];
    if (temp === undefined || temp2 === undefined) {
        return [];
    }
    array[index1] = temp2;
    array[index2] = temp;
    return array;
}

export class MarketTemplateSettingsFormState {
    private saveTemplateInProgress: PromiseExecutorQueue;

    public constructor(
        private readonly props: MarketTemplateSettingsPropsType,
        private readonly sdkStaff: SdkStaff,
        private readonly templateId: string
    ) {
        this.saveTemplateInProgress = new PromiseExecutorQueue();
    }

    public get currentTemplate(): MarketTemplateItem | null {
        const source = this.props.marketTemplatesState.marketTemplateByIt(this.templateId);
        return source === 'loading' ? null : source;
    }

    public get isNewTradingData(): boolean {
        return typeof this.currentTemplate?.selectionsNewTrading === 'string';
    }

    @computed public get selections(): MarketTemplateSelectionsType | undefined {
        // eslint-disable-next-line no-restricted-globals
        return this.currentTemplate?.selections.sort((a, b) => Number(b.displayOrder) - Number(a.displayOrder));
    }

    public get newTradingSelections(): Array<SelectionNewTradingType> {
        const rawSelections = this.currentTemplate?.selectionsNewTrading;
        if (rawSelections === null || rawSelections === undefined) {
            console.error('Selections for new trading are empty');
            return [];
        }
        return buildSelectionsNewTrading(rawSelections) ?? [];
    }

    public get allowedInMultiplesValues(): Record<string, string> {
        const list: Record<string, string> = {};
        const templates =
            this.props.marketTemplatesState.marketTemplates === 'loading'
                ? []
                : this.props.marketTemplatesState.marketTemplates;
        templates.map((item) => {
            list[
                item.matchingMarkets !== 'null' && item.matchingMarkets !== null && item.matchingMarkets !== undefined
                    ? item.matchingMarkets
                    : item.id
            ] = item.marketName;
        });

        return list;
    }

    public get allowedInMultiplesCurrentValues(): Array<string> {
        const list = this.currentTemplate?.allowedInMultiplesWith.map((item) => item.replaceAll('\\', ''));
        return list ?? [];
    }

    public onSaveTemplate = async (data: UpdateTemplateBody): Promise<void> => {
        this.saveTemplateInProgress.runActionInQue(async () => {
            const eventTemplateId = this.currentTemplate?.eventTemplate?.id;
            const currentTemplate = this.currentTemplate;
            if (eventTemplateId === undefined || currentTemplate === null) {
                return;
            }

            const isCashoutUpdate = Object.keys(data).includes('cashoutMarketSettings');
            if (isCashoutUpdate === true) {
                data = {
                    cashoutMarketSettings: {
                        ...currentTemplate.cashoutMarketSettings,
                        ...data.cashoutMarketSettings,
                    },
                };
            }

            const isEachWayUpdate = Object.keys(data).includes('eachWayEnabled');
            if (isEachWayUpdate === true) {
                const rawData = Object.values(data)[0];
                const offered = rawData !== undefined && rawData !== null && isBoolean(rawData) ? rawData : false;

                data = {
                    eachWaySettings: {
                        legacyOffered: this.currentTemplate?.eachWaySettings?.legacyOffered ?? true,
                        offered,
                        termsWithBet: false,
                    },
                };
            }

            const body: OpenapiProxyStaffMarketTemplateUpdateParamsType = {
                requestBody: {
                    eventTemplateId,
                    sportId: this.props.sportId,
                    marketTemplateId: this.templateId,
                    data: {
                        ...data,
                        matchingMarkets: this.currentTemplate?.matchingMarkets,
                    },
                },
            };
            await this.sdkStaff.patchMarketTemplate(body);
            await this.props.marketTemplatesState.templatesResource.refresh();
        });
    };

    public onSaveAllowedInMultiples = async (_target: unknown, value: Array<string>): Promise<void> => {
        await this.onSaveTemplate({ allowedInMultiplesWith: value });
    };

    public onTagChange = async (value: string, target: string): Promise<void> => {
        const tags = {
            ...this.currentTemplate?.tags,
            [target]: value,
        };

        await this.onSaveTemplate({ tags });
        await this.props.marketTemplatesState.templatesResource.refresh();
    };

    public onChangeSelections = async (data: UpdateSelectionsBody, selection: SelectionType): Promise<void> => {
        const eventTemplateId = this.currentTemplate?.eventTemplate?.id;

        if (eventTemplateId === undefined) {
            return;
        }

        await this.sdkStaff.patchMarketTemplateSelections({
            requestBody: {
                data,
                eventTemplateId,
                sportId: this.props.sportId,
                marketTemplateId: this.templateId,
                selectionId: selection.id,
            },
        });
    };

    public onChangeSelectionsNewTrading = async (
        selection: SelectionNewTradingType,
        newName?: string
    ): Promise<void> => {
        const eventTemplateId = this.currentTemplate?.eventTemplate?.id;

        if (eventTemplateId === undefined) {
            return;
        }

        const rawSelections = this.currentTemplate?.selectionsNewTrading;
        if (rawSelections === null || rawSelections === undefined) {
            return;
        }
        const allSelections = buildSelectionsNewTrading(rawSelections);
        const selections = allSelections?.map((item) => {
            const comparator = newName ?? selection.namePattern;
            if (comparator === item.namePattern) {
                return selection;
            }
            return item;
        });

        await this.sdkStaff.patchMarketTemplateSelections({
            requestBody: {
                data: {
                    selectionsNewTrading: JSON.stringify(selections),
                    matchingMarkets: this.currentTemplate?.matchingMarkets,
                },
                eventTemplateId,
                sportId: this.props.sportId,
                marketTemplateId: this.templateId,
                selectionId: selection.type,
            },
        });
    };

    public onChangeSelectionsOrder = async (data: OnChangeMeta): Promise<void> => {
        const eventTemplateId = this.currentTemplate?.eventTemplate?.id;
        const selections = this.currentTemplate?.selections;
        const reordered = swapElements(selections, data.oldIndex, data.newIndex);
        if (eventTemplateId === undefined) {
            return;
        }

        const body: Record<string, MarketTemplateSingleSelectionType> = {};
        reordered.forEach((item, index) => {
            item.displayOrder = 10000 - index;
        });

        await this.sdkStaff.patchMarketTemplateSelectionsReorder({
            requestBody: {
                data: body,
                eventTemplateId,
                sportId: this.props.sportId,
                marketTemplateId: this.templateId,
            },
        });
    };

    public onChangeSelectionsOrderNewTrading = async (data: OnChangeMeta): Promise<void> => {
        const eventTemplateId = this.currentTemplate?.eventTemplate?.id;
        const selections =
            this.newTradingSelections.length > 0 ? this.newTradingSelections : this.currentTemplate?.selections;
        // @ts-expect-error TODO: fix function return type
        const reordered = swapElements(selections, data.oldIndex, data.newIndex);
        if (eventTemplateId === undefined) {
            return;
        }

        reordered.forEach((item, index) => {
            // @ts-expect-error TODO: fix function return type
            item.metadata['displayOrder'] = index.toString();
        });

        await this.sdkStaff.patchMarketTemplateSelectionsReorder({
            requestBody: {
                data: {
                    all: {
                        selectionsNewTrading: JSON.stringify(reordered),
                        matchingMarkets: this.currentTemplate?.matchingMarkets,
                    },
                },
                eventTemplateId,
                sportId: this.props.sportId,
                marketTemplateId: this.templateId,
            },
        });

        await this.props.marketTemplatesState.templatesResource.refresh();
    };
}
